import React, { useCallback, useEffect, useRef, useState, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Import slides
import { Slide1_Category } from '../components/slides/Slide1_Category';
import { Slide2_Problem } from '../components/slides/Slide2_Problem';
import { Slide3_System } from '../components/slides/Slide3_System';
import { SystemPage } from './SystemPage';
import { Slide_RecruitingDocs } from '../components/slides/Slide_RecruitingDocs';
import { Slide5_Forge } from '../components/slides/Slide5_Forge';
import { Slide5_Proof } from '../components/slides/Slide5_Proof';
import { Slide7_Tiers } from '../components/slides/Slide7_Tiers';
import { Slide_Calculator } from '../components/slides/Slide_Calculator';
import { AdvantageProduct } from './AdvantageProduct';
import { AdvantageSummary } from './AdvantageSummary';
const SLIDES = [Slide1_Category, Slide2_Problem, Slide3_System, SystemPage, Slide_RecruitingDocs, Slide5_Forge, Slide5_Proof, Slide7_Tiers, Slide_Calculator, AdvantageProduct, AdvantageSummary];
const CALCULATOR_INDEX = SLIDES.indexOf(Slide_Calculator);
const SLIDE_LABELS = ['Capacity', 'Problem', 'Process', 'System', 'Frameworks', 'Sales School', 'Proof', 'Add-Ons', 'Calculator', 'Partnership', 'Proposal'];
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Warm the Google Drive connection up front so the Frameworks previews
  // aren't negotiating DNS and TLS at the moment the slide appears.
  useEffect(() => {
    const hosts = ['https://drive.google.com', 'https://lh3.googleusercontent.com'];
    const links = hosts.map(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach(link => link.remove());
  }, []);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Let typing in fields (e.g. the calculator inputs) use space and arrows.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  // The calculator stays mounted for the life of the deck (just hidden when
  // another tab is open) so its edits survive tab switches. It only resets
  // when a new instance of the deck is opened.
  const isCalculator = currentSlide === CALCULATOR_INDEX;
  const currentSlideRef = useRef(currentSlide);
  currentSlideRef.current = currentSlide;
  const [showCalculator, setShowCalculator] = useState(isCalculator);
  useEffect(() => {
    if (!isCalculator) setShowCalculator(false);
  }, [isCalculator]);
  // Reveal the calculator once the outgoing slide has finished exiting.
  const handleExitComplete = useCallback(() => {
    if (currentSlideRef.current === CALCULATOR_INDEX) setShowCalculator(true);
  }, []);
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 overflow-x-hidden relative selection:bg-[#7FE0C3]/30" data-id="element-1926" data-mp="3hg18dy54vd3g">
      {/* Main Content Area */}
      <div className="min-h-screen w-full relative flex flex-col" data-id="element-1927" data-mp="3cfnah8x8xjj5">
        <AnimatePresence mode="wait" onExitComplete={handleExitComplete} data-id="element-1928" data-mp="2frnsa7jwyjoj">
          {!isCalculator && <CurrentSlideComponent key={currentSlide} data-id="element-1929" data-mp="2jsra2zliupse" />}
        </AnimatePresence>
        <div className={isCalculator && showCalculator ? '' : 'hidden'} data-id="element-1930" data-mp="2oqsrzh77oj2a">
          <Slide_Calculator data-id="element-1931" data-mp="2thrbepnhxx9r" />
        </div>

        {/* Spacer for bottom nav */}
        <div className="h-32 shrink-0" data-id="element-1932" data-mp="2tju79qbbykme" />
      </div>

      {/* Navigation Controls (Bottom) - Mobile Optimized */}
      <div className="fixed bottom-0 left-0 w-full p-3 sm:p-6 flex flex-col space-y-3 z-50 pointer-events-none bg-gradient-to-t from-[#0A0F1C] via-[#0A0F1C]/90 to-transparent" data-id="element-1933" data-mp="33fx9ew66mj9c">
        {/* Slide Labels Navigation */}
        <div className="flex items-center justify-center pointer-events-auto" data-id="element-1934" data-mp="3ch57pe8p7k3f">
          <div className="flex items-center space-x-1 sm:space-x-2 bg-slate-900/90 backdrop-blur-sm border border-slate-800 rounded-full px-3 sm:px-4 py-2 shadow-xl overflow-x-auto max-w-full scrollbar-hide" data-id="element-1935" data-mp="1d3j1pvntrifj">
            {SLIDE_LABELS.map((label, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${idx === currentSlide ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} aria-label={`Go to ${label}`} data-id="element-1936" data-mp="1aknz7h56onmx" data-mpi={idx}>
                {label}
              </button>)}
          </div>
        </div>

        <p className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-slate-500" data-id="element-1937" data-mp="1ogrttbjyyxzx">
          High Impact Recruiting Engine
        </p>

        {/* Logo and Arrow Controls */}
        <div className="flex items-center justify-between pointer-events-auto" data-id="element-1938" data-mp="1fkipbih4x96x">
          <a href="https://thehireadvantage.co" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity" data-id="element-1939" data-mp="85xjri1kue9g">
            <img src="https://cdn.magicpatterns.com/uploads/f8eRPqMKBzam6b6SBPvfZ5/Full_white_logo.png" alt="D2D HIRE" className="h-6 sm:h-8 hover:opacity-90 transition-opacity" data-id="element-1940" data-mp="5ky2fqji101s" />
          </a>

          <div className="flex items-center space-x-2" data-id="element-1941" data-mp="kk5b3jfraij">
            <button onClick={prevSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Previous slide" data-id="element-1942" data-mp="n6cv36btq0r">
              <ChevronLeft size={20} data-id="element-1943" data-mp="392gde6ukin6f" />
            </button>

            <button onClick={nextSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Next slide" data-id="element-1944" data-mp="3dzgfk6oar5s9">
              <ChevronRight size={20} data-id="element-1945" data-mp="2ldi6vp49ayre" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}